import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";

const POUR_IMAGE =
  "https://i.ibb.co/3YNcnYtJ/Campo-de-olivos.png";

/**
 * "El oro verde" — static editorial photograph of the family olive grove.
 *
 * Previous version played a 10s autoplay loop of EVOO leaving the mill;
 * now displays a single still image to keep the page lighter and the
 * tone calmer / more editorial.
 */
export default function BottlePour() {
  const { t, lang } = useLang();

  // Pre-harvest neutral body text — process claims not yet confirmed for next harvest
  const neutralBody = lang === "es"
    ? "Cortijo Santa María elabora aceite de oliva virgen extra de cosecha temprana en Limones, Moclín (Granada). La próxima edición está en preparación."
    : "Cortijo Santa María produces early-harvest extra virgin olive oil in Limones, Moclín (Granada). The next edition is in preparation.";

  return (
    <section
      id="aceite-real"
      data-testid="bottle-pour-section"
      className="relative bg-cream-100 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 sm:py-32 lg:py-40 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Editorial copy */}
        <div className="lg:col-span-5">
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
            {t("bottlepour.overline")}
          </span>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] text-olive-900 font-medium">
            {t("bottlepour.title_a")} <br />
            <span className="italic">{t("bottlepour.title_b")}</span>.
          </h2>
          <p className="mt-8 text-base sm:text-lg text-olive-800/80 font-light max-w-md leading-relaxed">
            {PRE_HARVEST_MODE ? neutralBody : t("bottlepour.body")}
          </p>
          {!PRE_HARVEST_MODE && (
            <ul className="mt-10 space-y-5 text-olive-800/85 font-light text-base">
              {t("bottlepour.captions").map((c) => (
                <li key={c.k} className="flex items-start gap-4 border-b border-cream-400 pb-4 last:border-0">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-gold-deep font-medium shrink-0 mt-1">
                    {c.k}
                  </span>
                  <span className="leading-relaxed">{c.v}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Static editorial photograph of the grove */}
        <div className="lg:col-span-7">
          <figure className="relative bg-olive-900 overflow-hidden aspect-square sm:aspect-[4/5] lg:aspect-[5/6] shadow-[0_30px_80px_-30px_rgba(20,30,15,0.45)]">
            <img
              data-testid="bottle-pour-image"
              src={POUR_IMAGE}
              alt={t("bottlepour.video_alt")}
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-olive-900/40 via-transparent to-transparent" />
            <figcaption className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 text-cream-100/90 text-[10px] uppercase tracking-[0.32em] font-medium">
              {t("bottlepour.footer")}
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

